import React, { useEffect, useState, useCallback } from 'react'
import { Modal, Button } from 'react-bootstrap'
import Layout from '../../../../app/Layout'
import HeaderContent from '../../General/HeaderContent'
import Breadcrumb from '../../General/Breadcrumb'
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading'
import authAxios from '../../../../config/authAxios'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt    = n => Number(n || 0).toLocaleString('es-PE')
const pct    = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0
const fmtPct = n => `${Number(n || 0).toFixed(1)}%`
const mesLbl = ym => {
  if (!ym) return ''
  const [y, m] = ym.split('-')
  const N = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
  return `${N[+m]} ${y}`
}
const ini = n => n?.split(' ').slice(0, 2).map(x => x[0]?.toUpperCase() || '').join('') || '?'

const AVATAR = [
  'linear-gradient(135deg,#1E5FD8,#0EA5E9)',
  'linear-gradient(135deg,#F97316,#F59E0B)',
  'linear-gradient(135deg,#8B5CF6,#EC4899)',
  'linear-gradient(135deg,#10B981,#0EA5E9)',
  'linear-gradient(135deg,#EF4444,#F97316)',
  'linear-gradient(135deg,#0891B2,#06B6D4)',
]

// ── Semáforos ─────────────────────────────────────────────────────────────────
const VERDE    = 'green'
const AMARILLO = 'yellow'
const ROJO     = 'red'

const semInscritos  = (v, meta)      => v >= meta         ? VERDE : v >= meta * 0.8         ? AMARILLO : ROJO
const semConversion = (v, meta = 10) => v >= meta         ? VERDE : v >= meta * 0.8         ? AMARILLO : ROJO
const semRespuesta  = (v, meta = 35) => v >= meta         ? VERDE : v >= meta * 0.7         ? AMARILLO : ROJO
const semMaduracion = (v, meta = 3)  => v == null ? null  : v <= meta    ? VERDE : v <= meta + 1    ? AMARILLO : ROJO
const semContactab  = (v, meta = 10) => v == null ? null  : v <= meta    ? VERDE : v <= meta * 2    ? AMARILLO : ROJO
const semConvEfect  = (v, meta = 4)  => v >= meta         ? VERDE : v >= meta * 0.75        ? AMARILLO : ROJO
const semTratos     = (v, meta = 3)  => v >= meta         ? VERDE : v >= Math.max(1, meta - 1) ? AMARILLO : ROJO
const semProcesam   = (v, meta = 20) => v <= meta * 0.5   ? VERDE : v <= meta               ? AMARILLO : ROJO
const semAvanceSem  = (ins, res, mI, mR) =>
  (ins >= mI || res >= mR) ? VERDE : (ins >= mI * 0.67 || res >= mR * 0.7) ? AMARILLO : ROJO

const DOT_COLOR = { green: '#10B981', yellow: '#F59E0B', red: '#EF4444' }
const BG_COLOR  = { green: '#D1FAE5', yellow: '#FEF3C7', red: '#FEE2E2' }
const TXT_COLOR = { green: '#065F46', yellow: '#92400E', red: '#991B1B' }

// ── Dot semáforo ──────────────────────────────────────────────────────────────
function Dot({ s, size = 10 }) {
  if (!s) return null
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      borderRadius: '50%', background: DOT_COLOR[s],
      boxShadow: `0 0 0 3px ${DOT_COLOR[s]}33`, flexShrink: 0,
    }} />
  )
}

// ── Sección header ────────────────────────────────────────────────────────────
function SH({ num, title, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #E5E7EB' }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, background: '#1E5FD8',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, flexShrink: 0,
      }}>{num}</div>
      <div>
        <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#0F2044' }}>{title}</p>
        {sub && <p style={{ margin: 0, fontSize: 11, color: '#9CA3AF' }}>{sub}</p>}
      </div>
    </div>
  )
}

// ── Tarjeta KPI ejecutivo ─────────────────────────────────────────────────────
function KpiCard({ icon, label, value, meta, unit = '', semColor, brecha, accion }) {
  const bg = semColor ? BG_COLOR[semColor] : '#F8FAFC'
  const tc = semColor ? TXT_COLOR[semColor] : '#1E293B'
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 14px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {semColor && <Dot s={semColor} />}
            <span style={{ fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9CA3AF' }}>{label}</span>
          </div>
          {icon && <i className={`fas ${icon}`} style={{ color: '#D1D5DB', fontSize: 13 }} />}
        </div>
        <p style={{ fontWeight: 800, fontSize: 22, margin: '0 0 2px', color: tc, lineHeight: 1 }}>{value}</p>
        {meta != null && <p style={{ fontSize: 11, color: '#9CA3AF', margin: 0 }}>Meta: <strong>{meta}{unit}</strong></p>}
        {brecha != null && brecha !== 0 && (
          <p style={{ fontSize: 11, margin: '3px 0 0', color: semColor === VERDE ? '#10B981' : '#EF4444' }}>
            {brecha > 0 ? `+${brecha}${unit} sobre meta` : `Faltan ${Math.abs(brecha)}${unit}`}
          </p>
        )}
      </div>
      {accion && (
        <div style={{ padding: '8px 14px', borderTop: '1px solid #F3F4F6', background: bg }}>
          <p style={{ fontSize: 10, margin: 0, color: tc }}>
            <i className="fas fa-bolt" style={{ marginRight: 4 }} />{accion}
          </p>
        </div>
      )}
    </div>
  )
}

// ── Alerta card ───────────────────────────────────────────────────────────────
function AlertaCard({ tipo, mensaje, accion, severity }) {
  const icons = {
    baja_respuesta: 'fa-phone-slash', baja_conversion: 'fa-chart-line',
    asesor_riesgo: 'fa-user-times', avance_semanal: 'fa-calendar-week',
    contactabilidad: 'fa-stopwatch', cartera_desordenada: 'fa-inbox',
    fuente_ineficiente: 'fa-filter', trato_estancado: 'fa-pause-circle',
    reserva_en_riesgo: 'fa-exclamation-circle', asesor_baja_conv: 'fa-headset',
  }
  const bg = BG_COLOR[severity] || '#F8FAFC'
  const tc = TXT_COLOR[severity] || '#1E293B'
  return (
    <div style={{ borderRadius: 10, border: `1px solid ${DOT_COLOR[severity] || '#E5E7EB'}44`, background: bg, padding: '10px 12px', display: 'flex', gap: 10 }}>
      <i className={`fas ${icons[tipo] || 'fa-exclamation-triangle'}`} style={{ color: DOT_COLOR[severity], marginTop: 1, flexShrink: 0, fontSize: 13 }} />
      <div>
        <p style={{ margin: '0 0 2px', fontSize: 12, fontWeight: 600, color: tc }}>{mensaje}</p>
        <p style={{ margin: 0, fontSize: 10, color: tc, opacity: 0.8 }}>
          <i className="fas fa-arrow-right" style={{ marginRight: 4 }} />{accion}
        </p>
      </div>
    </div>
  )
}

// ── SVG Line chart ────────────────────────────────────────────────────────────
function LineChart({ data, keys, colors, labels, height = 170, agrupacion = 'semana' }) {
  if (!data?.length) return <p style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF', fontSize: 12 }}>Sin datos históricos</p>
  const W = 600, H = height, pad = { top: 12, right: 16, bottom: 48, left: 40 }
  const iW = W - pad.left - pad.right, iH = H - pad.top - pad.bottom
  const maxVal = Math.max(1, ...data.flatMap(d => keys.map(k => +d[k] || 0)))
  const xPos = i => data.length === 1 ? pad.left + iW / 2 : pad.left + (i / (data.length - 1)) * iW
  const yPos = v => pad.top + iH - (v / maxVal) * iH
  const pathD = key => data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xPos(i)},${yPos(+d[key] || 0)}`).join(' ')
  const MESES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  const fmtLbl = val => {
    const parts = (val || '').split('-')
    if (parts.length !== 3) return ''
    const [y, m, d] = parts
    if (agrupacion === 'mes') return `${MESES[+m]} ${y.slice(2)}`
    return `${d}/${m}`
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: W, display: 'block' }}>
        {[0, 25, 50, 75, 100].map(p => {
          const y = pad.top + iH * (1 - p / 100), v = Math.round(maxVal * p / 100)
          return <g key={p}>
            <line x1={pad.left} y1={y} x2={W - pad.right} y2={y} stroke="#E2E8F0" strokeWidth="1" />
            <text x={pad.left - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#94A3B8">{v}</text>
          </g>
        })}
        {data.map((d, i) => (
          <text key={i} x={xPos(i)} y={H - 26} textAnchor="middle" fontSize="9" fill="#94A3B8">{fmtLbl(d.sem_ini)}</text>
        ))}
        {keys.map((k, ki) => (
          <g key={k}>
            <path d={pathD(k)} fill="none" stroke={colors[ki]} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {data.map((d, i) => <circle key={i} cx={xPos(i)} cy={yPos(+d[k] || 0)} r="3.5" fill={colors[ki]} stroke="#fff" strokeWidth="2" />)}
          </g>
        ))}
        {keys.map((k, ki) => (
          <g key={k} transform={`translate(${pad.left + ki * 120},${H - 12})`}>
            <line x1="0" y1="6" x2="14" y2="6" stroke={colors[ki]} strokeWidth="2.5" />
            <circle cx="7" cy="6" r="3" fill={colors[ki]} />
            <text x="18" y="10" fontSize="9" fill="#64748B">{labels[ki]}</text>
          </g>
        ))}
      </svg>
    </div>
  )
}

// ── Barra horizontal ──────────────────────────────────────────────────────────
function HBar({ label, value, max, color = '#1E5FD8', rightLabel = '' }) {
  const w = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
        <span style={{ color: '#4B5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{label}</span>
        <span style={{ fontWeight: 700, fontFamily: 'monospace', color: '#374151', marginLeft: 8, flexShrink: 0 }}>{rightLabel || fmt(value)}</span>
      </div>
      <div style={{ height: 7, background: '#F1F5F9', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: 99 }} />
      </div>
    </div>
  )
}

// ── Embudo ejecutivo ──────────────────────────────────────────────────────────
function FunnelEjecutivo({ funnel }) {
  const { tot = 0, resp = 0, trat = 0, reservas = 0, conv = 0 } = funnel
  const steps = [
    { l: 'LEADS',     v: tot,      pctStr: '100%',                        bg: '#0F2044' },
    { l: 'RESPUESTA', v: resp,     pctStr: `${pct(resp, tot)}%`,           bg: '#1E5FD8' },
    { l: 'TRATOS',    v: trat,     pctStr: `${pct(trat, tot)}%`,           bg: '#0891B2' },
    { l: 'RESERVAS',  v: reservas, pctStr: `${pct(reservas, trat)}%`,      bg: '#7C3AED' },
    { l: 'INSCRITOS', v: conv,     pctStr: `${pct(conv, resp)}%`,          bg: '#10B981' },
  ]
  const maxV = Math.max(1, tot)
  return (
    <div>
      {steps.map((s, i) => {
        const w = Math.max(6, (s.v / maxV) * 100)
        const perdida = i > 0 ? steps[i - 1].v - s.v : 0
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ width: 72, textAlign: 'right', flexShrink: 0 }}>
              <span style={{ fontSize: 9, fontFamily: 'monospace', color: '#9CA3AF', textTransform: 'uppercase' }}>{s.l}</span>
            </div>
            <div style={{ flex: 1, height: 32, background: '#F1F5F9', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ width: `${w}%`, height: '100%', background: s.bg, minWidth: 56, display: 'flex', alignItems: 'center', paddingLeft: 12, borderRadius: 8 }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>{fmt(s.v)}</span>
              </div>
            </div>
            <div style={{ width: 80, flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: '#4B5563' }}>{s.pctStr}</span>
              {perdida > 0 && <span style={{ display: 'block', fontSize: 9, color: '#EF4444' }}>−{fmt(perdida)} pierden</span>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Ranking de asesores ───────────────────────────────────────────────────────
function RankingAsesores({ asesores, metas }) {
  if (!asesores?.length) return <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0', fontSize: 12 }}>Sin asesores</p>
  const metaMes    = metas?.meta_mensual        ?? 25
  const metaResSem = metas?.meta_reservas_sem   ?? 10
  const metaMad    = metas?.maduracion_max_dias  ?? 3
  const metaCont   = metas?.contactabilidad_max  ?? 10
  const metaHoras  = metas?.meta_horas_conv      ?? 4
  const metaProc   = metas?.procesamiento_max_pct ?? 20
  const cols = ['Asesor', 'Ins.Mes', 'Res.Sem', 'Conv%', 'Resp%', 'Mad.', 'Cont.', 'Conv.Ef.', 'Sin.Clas.', 'Acción']
  const thStyle = { fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', color: '#9CA3AF', paddingBottom: 8, paddingRight: 8, fontWeight: 600, borderBottom: '2px solid #E5E7EB', whiteSpace: 'nowrap' }
  const tdStyle = { paddingTop: 8, paddingBottom: 8, paddingRight: 8, borderTop: '1px solid #F9FAFB', verticalAlign: 'middle' }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000, fontSize: 12 }}>
        <thead><tr>{cols.map(h => <th key={h} style={{ ...thStyle, textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>
          {asesores.map((a, i) => {
            const respP  = pct(+a.resp, +a.leads)
            const convP  = pct(+a.conv, +a.resp)
            const cm     = +a.conv_mes        || 0
            const resSem = +a.reservas_semana || 0
            const mad    = a.mad_dias  != null ? +a.mad_dias  : null
            const cont   = a.cont_min  != null ? +a.cont_min  : null
            const hEf    = +a.conv_ef_hoy     || 0
            const sinCl  = +a.pct_sin_clas    || 0

            const sIns  = semInscritos(cm, metaMes)
            const sResp = semRespuesta(respP, metas?.pct_respuesta ?? 35)
            const sConv = semConversion(convP, metas?.pct_conversion ?? 10)
            const sRes  = resSem >= metaResSem ? VERDE : resSem >= metaResSem * 0.7 ? AMARILLO : ROJO
            const sMad  = semMaduracion(mad, metaMad)
            const sCont = semContactab(cont, metaCont)
            const sHEf  = semConvEfect(hEf, metaHoras)
            const sSin  = semProcesam(sinCl, metaProc)

            const accion = cm >= metaMes        ? 'Mantener ritmo ✓'
              : respP < 25                      ? 'Revisar primer contacto'
              : hEf < metaHoras * 0.5           ? 'Aumentar tiempo de llamadas'
              : convP < 8                       ? 'Reforzar técnica de cierre'
              :                                   `Faltan ${metaMes - cm} inscritos`

            const cell = (s, text) => (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Dot s={s} size={8} /><strong style={{ color: DOT_COLOR[s] }}>{text}</strong>
              </span>
            )

            return (
              <tr key={a.id_usuario}>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: AVATAR[i % AVATAR.length], color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{ini(a.nombre)}</div>
                    <span style={{ fontWeight: 600, color: '#0F2044', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{a.nombre}</span>
                  </div>
                </td>
                <td style={tdStyle}>{cell(sIns, `${cm}/${metaMes}`)}</td>
                <td style={tdStyle}>{cell(sRes, `${resSem}/${metaResSem}`)}</td>
                <td style={tdStyle}>{cell(sConv, `${convP}%`)}</td>
                <td style={tdStyle}>{cell(sResp, `${respP}%`)}</td>
                <td style={tdStyle}>{mad != null ? cell(sMad, `${mad}d`) : <span style={{ color: '#D1D5DB' }}>—</span>}</td>
                <td style={tdStyle}>{cont != null ? cell(sCont, `${cont}min`) : <span style={{ color: '#D1D5DB' }}>—</span>}</td>
                <td style={tdStyle}>{cell(sHEf, `${hEf}h`)}</td>
                <td style={tdStyle}>{cell(sSin, `${sinCl}%`)}</td>
                <td style={{ ...tdStyle, fontSize: 10, color: '#6B7280', maxWidth: 140 }}>{accion}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Tabla de fuentes ──────────────────────────────────────────────────────────
function FuentesTable({ fuentes }) {
  if (!fuentes?.length) return <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0', fontSize: 12 }}>Sin datos de fuentes</p>
  const maxLeads = Math.max(1, ...fuentes.map(f => +f.leads || 0))
  const thS = { fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', color: '#9CA3AF', paddingBottom: 8, paddingRight: 12, fontWeight: 600, borderBottom: '2px solid #E5E7EB' }
  const tdS = { paddingTop: 8, paddingBottom: 8, paddingRight: 12, borderTop: '1px solid #F9FAFB', verticalAlign: 'middle', fontSize: 12 }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
        <thead><tr>{['Fuente','Leads','Resp%','Inscritos','Conv%','Decisión'].map(h => <th key={h} style={{ ...thS, textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>
          {fuentes.map((f, i) => {
            const sResp = semRespuesta(+f.pct_resp || 0)
            const sConv = semConversion(+f.pct_conv || 0)
            const decision = (+f.pct_conv || 0) >= 10 ? 'Mantener inversión'
              : (+f.pct_resp || 0) < 25               ? 'Revisar o pausar'
              :                                          'Optimizar anuncio/landing'
            return (
              <tr key={i}>
                <td style={tdS}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: Math.max(4, (+f.leads / maxLeads) * 56), height: 6, background: '#1E5FD8', borderRadius: 3 }} />
                    <span style={{ fontWeight: 600, color: '#0F2044' }}>{f.nombre_fuente}</span>
                  </div>
                </td>
                <td style={{ ...tdS, fontFamily: 'monospace' }}>{fmt(f.leads)}</td>
                <td style={tdS}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Dot s={sResp} size={7} /><strong style={{ color: DOT_COLOR[sResp] }}>{f.pct_resp}%</strong></span></td>
                <td style={{ ...tdS, fontFamily: 'monospace' }}>{fmt(f.inscritos)}</td>
                <td style={tdS}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Dot s={sConv} size={7} /><strong style={{ color: DOT_COLOR[sConv] }}>{f.pct_conv}%</strong></span></td>
                <td style={{ ...tdS, fontSize: 10, color: '#6B7280' }}>{decision}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Lead caliente vs frío ─────────────────────────────────────────────────────
function LeadTipos({ tipos }) {
  if (!tipos?.length) return <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0', fontSize: 12 }}>Sin clasificación disponible</p>
  return (
    <div className="row">
      {tipos.map((t, i) => {
        const isHot = t.tipo === 'Caliente'
        const convRate = pct(+t.conv || 0, +t.resp || 0)
        return (
          <div key={i} className="col-sm-6">
            <div style={{ borderRadius: 10, border: `2px solid ${isHot ? '#F97316' : '#CBD5E1'}`, background: isHot ? '#FFF7ED' : '#F8FAFC', padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <i className={`fas ${isHot ? 'fa-fire' : 'fa-snowflake'}`} style={{ fontSize: 18, color: isHot ? '#F97316' : '#94A3B8' }} />
                <span style={{ fontWeight: 700, fontSize: 14, color: isHot ? '#C2410C' : '#475569' }}>Lead {t.tipo}</span>
              </div>
              <div className="row text-center" style={{ marginBottom: 10 }}>
                {[{ l: 'Leads', v: fmt(t.leads) }, { l: 'Contactados', v: `${t.pct_resp}%` }, { l: 'Conversión', v: `${convRate}%` }].map(x => (
                  <div key={x.l} className="col-4">
                    <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 8, padding: '8px 4px' }}>
                      <p style={{ margin: 0, fontWeight: 800, fontSize: 13, color: isHot ? '#C2410C' : '#475569' }}>{x.v}</p>
                      <p style={{ margin: 0, fontSize: 9, fontFamily: 'monospace', color: '#9CA3AF', textTransform: 'uppercase' }}>{x.l}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ margin: 0, fontSize: 10, color: '#6B7280', fontStyle: 'italic' }}>
                {isHot ? 'Contactar primero — asignar a asesores de mayor cierre' : 'Nutrición, remarketing y menor prioridad operativa'}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Procesamiento por estado ──────────────────────────────────────────────────
function ProcesamientoEstados({ estados, meta = 20 }) {
  if (!estados?.length) return null
  const total = estados.reduce((s, e) => s + (+e.cantidad || 0), 0)
  const maxCant = Math.max(1, ...estados.map(e => +e.cantidad || 0))
  const paleta = ['#0F2044', '#1E5FD8', '#0891B2', '#7C3AED', '#10B981', '#F97316', '#DC2626', '#94A3B8']
  const sinClasif = estados.find(e =>
    +e.id_estado === 0 || +e.id_estado === 1 ||
    (e.nombre_estado || '').toLowerCase().includes('sin') ||
    (e.nombre_estado || '').toLowerCase().includes('nuevo') ||
    (e.nombre_estado || '').toLowerCase().includes('pendiente')
  )
  const pctSin = sinClasif ? pct(+sinClasif.cantidad, total) : 0
  const sSin = semProcesam(pctSin, meta)
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 10, background: BG_COLOR[sSin] || '#F8FAFC', marginBottom: 16 }}>
        <Dot s={sSin} size={12} />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: DOT_COLOR[sSin] }}>{pctSin}% sin clasificar</p>
          <p style={{ margin: 0, fontSize: 10, color: TXT_COLOR[sSin] }}>
            {sSin === VERDE ? 'Cartera ordenada — mantener ritmo' : sSin === AMARILLO ? 'Atención: revisar asesores con más pendientes' : 'Cartera desordenada — exigir limpieza diaria'}
          </p>
        </div>
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#9CA3AF' }}>{fmt(total)} leads</span>
      </div>
      {estados.map((e, i) => (
        <HBar key={i}
          label={e.nombre_estado || `Estado ${e.id_estado}`}
          value={+e.cantidad || 0}
          max={maxCant}
          color={paleta[i % paleta.length]}
          rightLabel={`${fmt(e.cantidad)} (${pct(+e.cantidad, total)}%)`}
        />
      ))}
    </div>
  )
}

// ── Conv. efectiva por asesor ─────────────────────────────────────────────────
function ConvEfectivaAsesores({ asesores, meta = 4 }) {
  if (!asesores?.length) return <p style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', padding: '10px 0', fontStyle: 'italic' }}>Sin llamadas completadas hoy</p>
  return (
    <div>
      {asesores.map((a, i) => {
        const h = +a.horas_hoy || 0
        const w = Math.min(100, Math.round((h / meta) * 100))
        const color = h >= meta ? '#10B981' : h >= meta * 0.75 ? '#F59E0B' : '#EF4444'
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
              <span style={{ color: '#4B5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{a.nombre}</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, marginLeft: 8, color }}>{h}h</span>
            </div>
            <div style={{ height: 6, background: '#F1F5F9', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: 99 }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Tratos por fase (pipeline) ────────────────────────────────────────────────
function TratosFaseChart({ fases }) {
  if (!fases?.length) return <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0', fontSize: 12 }}>Sin tratos en pipeline</p>
  const total = fases.reduce((s, f) => s + (+f.cantidad || 0), 0)
  const maxVal = Math.max(1, ...fases.map(f => +f.cantidad || 0))
  const faseColor = nombre => {
    const n = (nombre || '').toLowerCase()
    if (n.includes('client') || n.includes('inscrit') || n.includes('cerrad')) return '#10B981'
    if (n.includes('reserv'))                                                   return '#7C3AED'
    if (n.includes('perd') || n.includes('descart') || n.includes('cancel'))   return '#EF4444'
    if (n.includes('prop') || n.includes('presup') || n.includes('cotiz'))     return '#F97316'
    if (n.includes('contact') || n.includes('resp'))                           return '#0891B2'
    return '#1E5FD8'
  }
  return (
    <div>
      {fases.map((f, i) => (
        <HBar key={i}
          label={f.fase}
          value={+f.cantidad || 0}
          max={maxVal}
          color={faseColor(f.fase)}
          rightLabel={`${fmt(f.cantidad)} · ${pct(+f.cantidad, total)}%`}
        />
      ))}
      <p style={{ fontSize: 10, fontFamily: 'monospace', color: '#9CA3AF', marginTop: 8 }}>{fmt(total)} tratos totales en pipeline</p>
    </div>
  )
}

// ── Cierre mensual ────────────────────────────────────────────────────────────
function CierreChart({ cierres, meses }) {
  if (!cierres?.length) return null
  const cols = meses?.length === 3 ? [
    { key: 'conv_ant2', label: mesLbl(meses[0]), color: '#CBD5E1' },
    { key: 'conv_ant',  label: mesLbl(meses[1]), color: '#93C5FD' },
    { key: 'conv_mes',  label: mesLbl(meses[2]), color: '#1E5FD8' },
  ] : []
  const maxVal = Math.max(1, ...cierres.flatMap(c => cols.map(col => +c[col.key] || 0)))
  const thS = { fontSize: 9, fontFamily: 'monospace', color: '#9CA3AF', textTransform: 'uppercase', paddingBottom: 8, fontWeight: 600, borderBottom: '1px solid #E5E7EB', textAlign: 'left' }
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        {cols.map(c => (
          <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: c.color, display: 'inline-block' }} />
            <span style={{ fontSize: 11, color: '#6B7280' }}>{c.label}</span>
          </div>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 340 }}>
          <thead><tr>
            <th style={{ ...thS }}>Asesor</th>
            {cols.map(c => <th key={c.key} style={{ ...thS, textAlign: 'right', paddingLeft: 12 }}>{c.label}</th>)}
          </tr></thead>
          <tbody>
            {cierres.map((c, i) => (
              <tr key={i} style={{ borderTop: '1px solid #F9FAFB' }}>
                <td style={{ padding: '8px 0', fontSize: 12, fontWeight: 600, color: '#374151' }}>{c.nombre}</td>
                {cols.map(col => {
                  const v = +c[col.key] || 0
                  const w = Math.max(4, (v / maxVal) * 64)
                  return (
                    <td key={col.key} style={{ padding: '8px 0 8px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                        <div style={{ height: 16, width: w, background: col.color, borderRadius: 3 }} />
                        <span style={{ fontWeight: 700, color: '#374151', fontSize: 12, width: 20, textAlign: 'right' }}>{v}</span>
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Modal de metas ────────────────────────────────────────────────────────────
function MetasModal({ idCamp, metasActuales, onClose, onSaved }) {
  const [form, setForm] = useState({
    pct_respuesta_objetivo:             metasActuales?.pct_respuesta        ?? 35,
    pct_conversion_objetivo:            metasActuales?.pct_conversion       ?? 10,
    meta_llamadas_diarias:              metasActuales?.meta_marcaciones_dia ?? 70,
    meta_tratos_diarios:                metasActuales?.meta_tratos_dia      ?? 3,
    meta_cierres_diarios:               metasActuales?.meta_cierres_dia     ?? 2,
    meta_conversiones_mensuales_asesor: metasActuales?.meta_mensual         ?? 25,
    total_leads:                        metasActuales?.total_leads          ?? 0,
    meta_horas_conv:                    metasActuales?.meta_horas_conv      ?? 4,
    meta_inscritos_sem:                 metasActuales?.meta_inscritos_sem   ?? 6,
    meta_reservas_sem:                  metasActuales?.meta_reservas_sem    ?? 10,
    maduracion_max_dias:                metasActuales?.maduracion_max_dias  ?? 3,
    contactabilidad_max:                metasActuales?.contactabilidad_max  ?? 10,
  })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg]       = useState(null)

  const handleChange = e => {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: +value }))
  }

  const handleSave = async () => {
    if (!idCamp) { setMsg({ ok: false, text: 'Este producto no tiene campaña activa registrada' }); return }
    setSaving(true); setMsg(null)
    try {
      const r = await authAxios.post('/admin/kpis-ventas/metas', { id_camp: idCamp, ...form })
      if (r.data?.success) {
        setMsg({ ok: true, text: 'Metas guardadas correctamente' })
        setTimeout(() => { onSaved(); onClose() }, 1000)
      } else {
        setMsg({ ok: false, text: r.data?.error || 'Error al guardar' })
      }
    } catch { setMsg({ ok: false, text: 'Error de conexión' }) }
    finally { setSaving(false) }
  }

  const fieldGroups = [
    {
      title: 'Metas de campaña',
      fields: [
        { key: 'pct_respuesta_objetivo',             label: 'Meta Respuesta (%)',            min: 1,  max: 100   },
        { key: 'pct_conversion_objetivo',            label: 'Meta Conversión (%)',            min: 1,  max: 100   },
        { key: 'meta_conversiones_mensuales_asesor', label: 'Meta Inscritos/mes por asesor', min: 1,  max: 200   },
        { key: 'total_leads',                        label: 'Total leads proyectados',        min: 0,  max: 99999 },
      ],
    },
    {
      title: 'Metas semanales',
      fields: [
        { key: 'meta_inscritos_sem', label: 'Inscritos por semana', min: 1, max: 50  },
        { key: 'meta_reservas_sem',  label: 'Reservas por semana',  min: 1, max: 100 },
      ],
    },
    {
      title: 'Metas diarias',
      fields: [
        { key: 'meta_llamadas_diarias', label: 'Llamadas/día',             min: 1, max: 500 },
        { key: 'meta_tratos_diarios',   label: 'Tratos/día',               min: 1, max: 50  },
        { key: 'meta_cierres_diarios',  label: 'Cierres/día',              min: 1, max: 50  },
        { key: 'meta_horas_conv',       label: 'Conv. efectiva (h/asesor)', min: 1, max: 12  },
      ],
    },
    {
      title: 'Umbrales de calidad',
      fields: [
        { key: 'maduracion_max_dias', label: 'Maduración máx (días)',     min: 1, max: 30  },
        { key: 'contactabilidad_max', label: 'Contactabilidad máx (min)', min: 1, max: 120 },
      ],
    },
  ]

  return (
    <Modal show onHide={onClose} size="md">
      <Modal.Header style={{ background: 'linear-gradient(135deg,#0F2044,#1E5FD8)', color: '#fff', borderBottom: 'none' }}>
        <div>
          <p style={{ margin: 0, fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.5)' }}>Configuración</p>
          <Modal.Title style={{ color: '#fff', fontSize: 18 }}>Metas de Campaña</Modal.Title>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 20, cursor: 'pointer' }}>×</button>
      </Modal.Header>
      {!idCamp && (
        <div style={{ background: '#FEF3C7', padding: '8px 16px', fontSize: 11, color: '#92400E' }}>
          <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }} />
          Este producto no tiene campaña activa — los cambios no se guardarán en BD
        </div>
      )}
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', padding: 20 }}>
        {fieldGroups.map(group => (
          <div key={group.title} style={{ marginBottom: 20 }}>
            <p style={{ margin: '0 0 8px', fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#60A5FA', borderBottom: '1px solid #EFF6FF', paddingBottom: 4 }}>{group.title}</p>
            {group.fields.map(f => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <label style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>{f.label}</label>
                <input type="number" name={f.key} value={form[f.key]} min={f.min} max={f.max}
                  onChange={handleChange}
                  className="form-control" style={{ width: 90, textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }} />
              </div>
            ))}
          </div>
        ))}
        {msg && (
          <div style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, background: msg.ok ? '#F0FDF4' : '#FEF2F2', color: msg.ok ? '#166534' : '#991B1B', marginTop: 8 }}>
            <i className={`fas ${msg.ok ? 'fa-check-circle' : 'fa-exclamation-circle'}`} style={{ marginRight: 8 }} />{msg.text}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !idCamp}>
          {saving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Guardando...</> : <><i className="fas fa-save" style={{ marginRight: 6 }} />Guardar metas</>}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
//  PÁGINA PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
const ReporteKpisVentas = () => {
  const [proyectos,   setProyectos]   = useState([])
  const [globalKpis,  setGlobalKpis]  = useState(null)
  const [pidSel,      setPidSel]      = useState(null)
  const [prodData,    setProdData]    = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [loadingProd, setLoadingProd] = useState(false)
  const [error,       setError]       = useState(null)
  const [modalMetas,  setModalMetas]  = useState(false)
  const [histFiltros, setHistFiltros] = useState({ agrupacion: 'semana', asesor: 0, fuente: 0, preset: '30d' })
  const [histData,    setHistData]    = useState([])
  const [histLoading, setHistLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      authAxios.get('/admin/kpis-ventas/proyectos'),
      authAxios.get('/admin/kpis-ventas/global'),
    ])
      .then(([rP, rG]) => {
        const proy = rP.data?.data ?? []
        setProyectos(proy)
        setGlobalKpis(rG.data?.data ?? null)
        if (proy.length > 0) setPidSel(proy[0].id_proyecto)
      })
      .catch(() => setError('No se pudo conectar con la API'))
      .finally(() => setLoading(false))
  }, [])

  const cargarProducto = useCallback(pid => {
    if (!pid) return
    setLoadingProd(true); setProdData(null)
    authAxios.get(`/admin/kpis-ventas/producto?pid=${pid}`)
      .then(r => setProdData(r.data?.data ?? null))
      .catch(() => setProdData(null))
      .finally(() => setLoadingProd(false))
  }, [])

  useEffect(() => { if (pidSel) cargarProducto(pidSel) }, [pidSel, cargarProducto])

  const cargarHistorico = useCallback((pid, filtros) => {
    if (!pid) return
    setHistLoading(true)
    const hoy   = new Date()
    const hasta = hoy.toISOString().slice(0, 10)
    const presets = { '7d': 7, '30d': 30, '60d': 60, '90d': 90 }
    const dias  = presets[filtros.preset] ?? 30
    const desde = new Date(hoy - dias * 864e5).toISOString().slice(0, 10)
    const q = new URLSearchParams({
      pid, agrupacion: filtros.agrupacion, desde, hasta,
      ...(filtros.asesor > 0 ? { asesor: filtros.asesor } : {}),
      ...(filtros.fuente > 0 ? { fuente: filtros.fuente } : {}),
    })
    authAxios.get(`/admin/kpis-ventas/historico?${q}`)
      .then(r => setHistData(r.data?.data ?? []))
      .catch(() => setHistData([]))
      .finally(() => setHistLoading(false))
  }, [])

  useEffect(() => {
    if (pidSel) cargarHistorico(pidSel, histFiltros)
  }, [pidSel, histFiltros, cargarHistorico])

  const prodActual = proyectos.find(p => p.id_proyecto === pidSel)

  const handleCSV = () => {
    const sep  = ','
    const q    = v => `"${String(v ?? '').replace(/"/g, '""')}"`
    const rows = [
      [q('REPORTE KPI VENTAS'), q(prodActual?.nombre_proyecto || ''), q(new Date().toLocaleDateString('es-PE'))],
      [],
      [q('=== FUNNEL ===')],
      ['Leads', 'Respuesta', 'Tratos', 'Reservas', 'Inscritos'].map(q),
      [funnel.tot, funnel.resp, funnel.trat, funnel.reservas, funnel.conv].map(q),
      [],
      [q('=== ASESORES ===')],
      ['Nombre', 'Leads', 'Resp%', 'Conv%', 'Ins.Mes', 'Reservas'].map(q),
      ...asesores.map(a => [q(a.nombre), q(a.leads), q(pct(+a.resp, +a.leads) + '%'), q(pct(+a.conv, +a.resp) + '%'), q(a.conv_mes), q(a.reservas || 0)]),
      [],
      [q('=== FUENTES ===')],
      ['Fuente', 'Leads', 'Resp%', 'Inscritos', 'Conv%'].map(q),
      ...fuentes.map(f => [q(f.nombre_fuente), q(f.leads ?? 0), q((f.pct_resp ?? 0) + '%'), q(f.inscritos ?? 0), q((f.pct_conv ?? 0) + '%')]),
    ]
    const csv  = rows.map(r => r.join(sep)).join('\r\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = Object.assign(document.createElement('a'), {
      href: url,
      download: `kpis-ventas-${(prodActual?.nombre_proyecto || 'reporte').replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`,
    })
    document.body.appendChild(a); a.click()
    document.body.removeChild(a); URL.revokeObjectURL(url)
  }

  // Extraer datos ───────────────────────────────────────────────────────────
  const g            = globalKpis || {}
  const d            = prodData   || {}
  const funnel       = d.funnel           || {}
  const metas        = d.metas            || {}
  const asesores     = d.asesores         || []
  const tratosFase   = d.tratos_fase      || []
  const cierres      = d.cierres          || []
  const meses        = d.meses            || []
  const avanceSem    = d.avance_semanal   || {}
  const maduracion   = d.maduracion       || {}
  const contactab    = d.contactabilidad  || {}
  const convEfectiva = d.conv_efectiva    || {}
  const prodHoy      = d.productividad_hoy|| {}
  const procesam     = d.procesamiento    || []
  const fuentes      = d.fuentes          || []
  const tiposLead    = d.tipos_lead       || []
  const alertas      = d.alertas          || []

  const totLeads     = +funnel.tot  || 0
  const totResp      = +funnel.resp || 0
  const totConv      = +funnel.conv || 0
  const pctResp      = pct(totResp, totLeads)
  const pctConv      = pct(totConv, totResp)
  const metaMes      = metas.meta_mensual        ?? 25
  const metaInsSem   = metas.meta_inscritos_sem  ?? 6
  const metaResSem   = metas.meta_reservas_sem   ?? 10
  const numAsesores  = asesores.length || 1
  const totalMeta    = metaMes * numAsesores
  const totalConvMes = asesores.reduce((s, a) => s + (+a.conv_mes || 0), 0)
  const madDias      = maduracion.promedio_dias    != null ? +maduracion.promedio_dias    : null
  const contMin      = contactab.min_promedio      != null ? +contactab.min_promedio      : null
  const horasConv    = convEfectiva.horas_hoy      != null ? +convEfectiva.horas_hoy      : null
  const hXAsesor     = convEfectiva.horas_x_asesor != null ? +convEfectiva.horas_x_asesor : null
  const pctSinClas   = (() => {
    const tot = procesam.reduce((s, e) => s + (+e.cantidad || 0), 0)
    const sin = procesam.find(e =>
      +e.id_estado === 0 || +e.id_estado === 1 ||
      (e.nombre_estado || '').toLowerCase().includes('sin') ||
      (e.nombre_estado || '').toLowerCase().includes('nuevo') ||
      (e.nombre_estado || '').toLowerCase().includes('pendiente')
    )
    return tot > 0 ? pct(+sin?.cantidad || 0, tot) : 0
  })()

  // Estilos reutilizables ───────────────────────────────────────────────────
  const cardStyle = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, marginBottom: 20 }
  const btnTabStyle = active => ({
    padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    background: active ? '#1E5FD8' : '#fff', color: active ? '#fff' : '#6B7280',
    border: '1px solid #E5E7EB', borderRadius: 0,
  })
  const miniCardStyle = sem => ({
    background: sem ? BG_COLOR[sem] : '#fff',
    border: `1px solid ${sem ? DOT_COLOR[sem] + '44' : '#E5E7EB'}`,
    borderRadius: 10, padding: '12px 14px', textAlign: 'center',
  })

  return (
    <Layout>
      <div className="ControlReportes">
        <div className="ControlReportes__header">
          <HeaderContent title="KPI's de Ventas" />
          <Breadcrumb
            link={[
              { url: '/inicio', nombre: 'Inicio', icon: 'fas fa-home' },
              { url: '', nombre: 'Reportes' },
              { url: '', nombre: "KPI's de Ventas" },
            ]}
          />
        </div>

        <div className="ControlReportes__body">
          <SpinnerLoading loading={loading}>

            {error && (
              <div style={cardStyle}>
                <p style={{ color: '#EF4444', textAlign: 'center', fontSize: 13 }}>
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }} />{error}
                </p>
              </div>
            )}

            {!error && (
              <>
                {/* ── KPIs globales ── */}
                <div className="row mb-3">
                  {[
                    { label: 'Total Leads',   value: fmt(g.total_leads),    sub: 'sin fuente cartera',                                      color: '#0F2044' },
                    { label: 'Con Respuesta', value: fmt(g.con_respuesta),   sub: `${pct(+g.con_respuesta, +g.total_leads)}% de respuesta`,  color: '#1E5FD8' },
                    { label: 'Convertidos',   value: fmt(g.convertidos),     sub: `${pct(+g.convertidos, +g.con_respuesta)}% de conversión`, color: '#10B981' },
                    { label: 'Programas',     value: proyectos.length,       sub: 'con leads activos',                                       color: '#7C3AED' },
                  ].map(x => (
                    <div key={x.label} className="col-6 col-md-3 mb-2">
                      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' }}>
                        <p style={{ margin: '0 0 2px', fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9CA3AF' }}>{x.label}</p>
                        <p style={{ margin: '0 0 2px', fontSize: 22, fontWeight: 800, color: x.color }}>{x.value}</p>
                        {x.sub && <p style={{ margin: 0, fontSize: 11, color: '#9CA3AF' }}>{x.sub}</p>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── Selector de programa ── */}
                <div style={cardStyle}>
                  <p style={{ margin: '0 0 10px', fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#9CA3AF' }}>Seleccionar programa</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {proyectos.map(p => (
                      <button key={p.id_proyecto} onClick={() => setPidSel(p.id_proyecto)}
                        style={{
                          padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          background: pidSel === p.id_proyecto ? '#1E5FD8' : '#fff',
                          border: `1px solid ${pidSel === p.id_proyecto ? '#1E5FD8' : '#E5E7EB'}`,
                          color: pidSel === p.id_proyecto ? '#fff' : '#1E5FD8',
                        }}>
                        {p.nombre_proyecto}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Botones de acción ── */}
                {prodData && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                    {prodData.id_camp && (
                      <button onClick={() => setModalMetas(true)}
                        className="btn btn-sm btn-outline-secondary">
                        <i className="fas fa-cog" style={{ marginRight: 6 }} />Metas
                      </button>
                    )}
                    <button onClick={handleCSV} className="btn btn-sm btn-outline-secondary">
                      <i className="fas fa-file-csv" style={{ marginRight: 6 }} />Exportar CSV
                    </button>
                    <button onClick={() => window.print()} className="btn btn-sm btn-outline-secondary">
                      <i className="fas fa-print" style={{ marginRight: 6 }} />Imprimir
                    </button>
                    <button onClick={() => pidSel && cargarProducto(pidSel)} className="btn btn-sm btn-outline-secondary">
                      <i className="fas fa-sync-alt" style={{ marginRight: 6 }} />Actualizar
                    </button>
                  </div>
                )}

                <SpinnerLoading loading={loadingProd}>
                  {prodData && (
                    <>
                      {/* ── Header producto ── */}
                      <div style={{ borderRadius: 12, color: '#fff', padding: '20px 24px', marginBottom: 20, background: 'linear-gradient(135deg,#0F2044 0%,#1A3260 60%,#1E3A6E 100%)' }}>
                        <p style={{ margin: '0 0 2px', fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.5)' }}>Programa activo</p>
                        <h4 style={{ margin: '0 0 2px', fontWeight: 800, color: '#fff' }}>{prodActual?.nombre_proyecto}</h4>
                        {prodActual?.camp_inicio && (
                          <p style={{ margin: '0 0 14px', fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>
                            Campaña desde {prodActual.camp_inicio.slice(0, 10).split('-').reverse().join('/')}
                          </p>
                        )}
                        <div className="row">
                          {[
                            { l: 'Leads',     v: fmt(totLeads),     s: 'total campaña' },
                            { l: 'Respuesta', v: `${pctResp}%`,     s: `meta ${metas.pct_respuesta || 35}%` },
                            { l: 'Conversión', v: `${pctConv}%`,    s: `meta ${metas.pct_conversion || 10}%` },
                            { l: 'Inscritos', v: fmt(totalConvMes), s: `meta ${fmt(totalMeta)}` },
                            { l: 'Tratos',    v: fmt(funnel.trat || 0), s: 'en pipeline' },
                          ].map(x => (
                            <div key={x.l} className="col-6 col-md mb-2">
                              <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '10px 12px' }}>
                                <p style={{ margin: '0 0 2px', fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>{x.l}</p>
                                <p style={{ margin: '0 0 2px', fontSize: 20, fontWeight: 800, color: '#fff' }}>{x.v}</p>
                                <p style={{ margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{x.s}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* ── SECCIÓN 1: Resumen ejecutivo (10 KPIs) ── */}
                      <div style={cardStyle}>
                        <SH num="1" title="Resumen Ejecutivo" sub="KPIs jerarquizados por impacto en el resultado de campaña" />
                        <div className="row mb-3">
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-user-check" label="Inscritos mensuales"
                              value={`${fmt(totalConvMes)} / ${fmt(totalMeta)}`}
                              meta={totalMeta} unit=" ventas"
                              semColor={semInscritos(totalConvMes, totalMeta)}
                              brecha={totalConvMes - totalMeta}
                              accion={totalConvMes >= totalMeta ? 'Mantener ritmo y asignar leads calientes' : `Faltan ${totalMeta - totalConvMes} para la meta del equipo`} />
                          </div>
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-calendar-week" label="Avance semanal"
                              value={`${avanceSem.inscritos_semana || 0}ins / ${avanceSem.reservas_semana || 0}res`}
                              meta={`${metaInsSem} ins o ${metaResSem} res`}
                              semColor={semAvanceSem(+avanceSem.inscritos_semana || 0, +avanceSem.reservas_semana || 0, metaInsSem, metaResSem)}
                              accion={`Meta: ${metaInsSem} inscritos o ${metaResSem} reservas por semana`} />
                          </div>
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-chart-line" label="Conversión global"
                              value={`${pctConv}%`} meta={`${metas.pct_conversion || 10}%`}
                              semColor={semConversion(pctConv, metas.pct_conversion || 10)}
                              brecha={pctConv - (metas.pct_conversion || 10)}
                              accion={pctConv < 8 ? 'Revisar cierre, objeciones y calidad de asesoría' : pctConv < 10 ? 'Reforzar seguimiento de reservas' : 'Mantener'} />
                          </div>
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-phone-alt" label="Respuesta global"
                              value={`${pctResp}%`} meta={`${metas.pct_respuesta || 35}%`}
                              semColor={semRespuesta(pctResp, metas.pct_respuesta || 35)}
                              brecha={pctResp - (metas.pct_respuesta || 35)}
                              accion={pctResp < 25 ? 'Urgente: revisar primer contacto y fuentes' : 'Mejorar horarios y mensaje de apertura'} />
                          </div>
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-hourglass-half" label="Maduración prom."
                              value={madDias != null ? `${madDias} días` : 'Sin datos'}
                              meta={`≤ ${metas.maduracion_max_dias || 3} días`}
                              semColor={semMaduracion(madDias, metas.maduracion_max_dias || 3)}
                              accion={madDias != null && madDias > (metas.maduracion_max_dias || 3) ? 'Priorizar tratos estancados y acelerar seguimiento' : 'Dentro del objetivo'} />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-clock" label="Contactabilidad"
                              value={contMin != null ? `${contMin} min` : 'Sin datos'}
                              meta={`≤ ${metas.contactabilidad_max || 10} min`}
                              semColor={semContactab(contMin, metas.contactabilidad_max || 10)}
                              accion={contMin != null && contMin > (metas.contactabilidad_max || 10) ? 'Reasignar leads — revisar disponibilidad en horas pico' : 'Tiempo de contacto óptimo'} />
                          </div>
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-headset" label="Conv. efectiva hoy"
                              value={horasConv != null ? `${horasConv}h` : '0h'}
                              meta={`≥ ${metas.meta_horas_conv || 4} h/asesor`}
                              semColor={semConvEfect(hXAsesor ?? 0, metas.meta_horas_conv || 4)}
                              brecha={hXAsesor != null ? +(hXAsesor - (metas.meta_horas_conv || 4)).toFixed(1) : null}
                              accion={!hXAsesor ? 'Sin llamadas completadas hoy' : hXAsesor >= (metas.meta_horas_conv || 4) ? `${hXAsesor}h/asesor — ritmo óptimo` : `${hXAsesor}h/asesor — faltan ${((metas.meta_horas_conv || 4) - hXAsesor).toFixed(1)}h`} />
                          </div>
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-handshake" label="Tratos hoy"
                              value={fmt(prodHoy.tratos_hoy || 0)} meta={`≥ ${metas.meta_tratos_dia || 3}`}
                              semColor={semTratos(+prodHoy.tratos_hoy || 0, metas.meta_tratos_dia || 3)}
                              accion={+prodHoy.tratos_hoy < (metas.meta_tratos_dia || 3) ? 'Auditar llamadas y mensajes — sin oportunidades suficientes' : 'Ritmo de tratos correcto'} />
                          </div>
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-tasks" label="Sin clasificar"
                              value={`${pctSinClas}%`} meta={`≤ ${metas.procesamiento_max_pct || 20}%`}
                              semColor={semProcesam(pctSinClas, metas.procesamiento_max_pct || 20)}
                              accion={pctSinClas > (metas.procesamiento_max_pct || 20) ? 'Exigir limpieza diaria de cartera' : pctSinClas > 10 ? 'Recordar procesamiento de leads' : 'Cartera en orden'} />
                          </div>
                          <div className="col-6 col-md-4 col-lg mb-2">
                            <KpiCard icon="fa-phone" label="Marcaciones hoy"
                              value={fmt(prodHoy.marcaciones_hoy || 0)} meta={`≥ ${metas.meta_marcaciones_dia || 70}`}
                              semColor={+prodHoy.marcaciones_hoy >= (metas.meta_marcaciones_dia || 70) ? VERDE : +prodHoy.marcaciones_hoy >= (metas.meta_marcaciones_dia || 70) * 0.7 ? AMARILLO : ROJO}
                              accion="Indicador operativo — usar para explicar baja conversación" />
                          </div>
                        </div>
                      </div>

                      {/* ── SECCIÓN 2: Alertas ── */}
                      {alertas.length > 0 && (
                        <div style={cardStyle}>
                          <SH num="2" title="Alertas Automáticas" sub={`${alertas.length} alerta${alertas.length > 1 ? 's' : ''} detectada${alertas.length > 1 ? 's' : ''}`} />
                          <div className="row">
                            {alertas.map((a, i) => (
                              <div key={i} className="col-md-6 mb-2">
                                <AlertaCard {...a} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ── SECCIÓN 3: Ranking de asesores ── */}
                      <div style={cardStyle}>
                        <SH num="3" title="Ranking de Asesores" sub="Comparativo de rendimiento con semáforo y acción sugerida" />
                        <RankingAsesores asesores={asesores} metas={metas} />
                      </div>

                      {/* ── SECCIONES 4 y 5: Embudo + Procesamiento ── */}
                      <div className="row mb-0">
                        <div className="col-lg-6 mb-4">
                          <div style={{ ...cardStyle, marginBottom: 0, height: '100%' }}>
                            <SH num="4" title="Embudo Ejecutivo" sub="Pérdida por etapa: leads → inscritos" />
                            <FunnelEjecutivo funnel={funnel} />
                            <div className="row mt-3">
                              <div className="col-6">
                                <div style={{ background: '#EFF6FF', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                                  <p style={{ margin: '0 0 2px', fontSize: 9, fontFamily: 'monospace', color: '#60A5FA', textTransform: 'uppercase' }}>Meta Respuesta</p>
                                  <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1D4ED8' }}>{metas.pct_respuesta || 35}%</p>
                                </div>
                              </div>
                              <div className="col-6">
                                <div style={{ background: '#F0FDF4', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                                  <p style={{ margin: '0 0 2px', fontSize: 9, fontFamily: 'monospace', color: '#34D399', textTransform: 'uppercase' }}>Meta Conversión</p>
                                  <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#065F46' }}>{metas.pct_conversion || 10}%</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-6 mb-4">
                          <div style={{ ...cardStyle, marginBottom: 0, height: '100%' }}>
                            <SH num="5" title="Procesamiento de Leads" sub="Distribución por estado — detectar cartera abandonada" />
                            <ProcesamientoEstados estados={procesam} meta={metas.procesamiento_max_pct || 20} />
                          </div>
                        </div>
                      </div>

                      {/* ── SECCIÓN 6: Fuentes ── */}
                      <div style={cardStyle}>
                        <SH num="6" title="Rendimiento por Fuente" sub="Respuesta, conversión e inscritos por origen de lead" />
                        <FuentesTable fuentes={fuentes} />
                      </div>

                      {/* ── SECCIONES 7-8: Pipeline + Lead Caliente ── */}
                      <div className="row mb-0">
                        <div className="col-lg-6 mb-4">
                          <div style={{ ...cardStyle, marginBottom: 0, height: '100%' }}>
                            <SH num="7" title="Pipeline de Tratos" sub="Distribución actual de tratos por fase del proceso de venta" />
                            <TratosFaseChart fases={tratosFase} />
                          </div>
                        </div>
                        <div className="col-lg-6 mb-4">
                          <div style={{ ...cardStyle, marginBottom: 0, height: '100%' }}>
                            <SH num="8" title="Lead Caliente vs Lead Frío" sub="Caliente = vino por bot de WhatsApp (ManyChat) · Frío = fuente directa" />
                            <LeadTipos tipos={tiposLead} />
                          </div>
                        </div>
                      </div>

                      {/* ── SECCIÓN 9: Productividad de hoy ── */}
                      <div style={cardStyle}>
                        <SH num="9" title="Productividad de Hoy" sub={new Date().toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' })} />
                        <div className="row mb-3">
                          {[
                            { l: 'Tratos',      v: prodHoy.tratos_hoy || 0,     meta: metas.meta_tratos_dia || 3,     s: semTratos(+prodHoy.tratos_hoy || 0, metas.meta_tratos_dia || 3),    icon: 'fa-handshake' },
                            { l: 'Marcaciones', v: prodHoy.marcaciones_hoy || 0, meta: metas.meta_marcaciones_dia || 70, s: +prodHoy.marcaciones_hoy >= (metas.meta_marcaciones_dia || 70) ? VERDE : +prodHoy.marcaciones_hoy >= (metas.meta_marcaciones_dia || 70) * 0.7 ? AMARILLO : ROJO, icon: 'fa-phone' },
                            { l: 'Inscritos',   v: prodHoy.inscritos_hoy || 0,   meta: 1,                             s: +(prodHoy.inscritos_hoy || 0) >= 1 ? VERDE : AMARILLO,              icon: 'fa-user-check' },
                            { l: 'Reservas',    v: prodHoy.reservas_hoy || 0,    meta: null,                          s: null,                                                                icon: 'fa-bookmark' },
                          ].map(x => (
                            <div key={x.l} className="col-6 col-md-3 mb-2">
                              <div style={miniCardStyle(x.s)}>
                                <i className={`fas ${x.icon}`} style={{ color: '#D1D5DB', fontSize: 18, marginBottom: 6, display: 'block' }} />
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 2 }}>
                                  {x.s && <Dot s={x.s} size={8} />}
                                  <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0F2044' }}>{fmt(x.v)}</p>
                                </div>
                                <p style={{ margin: 0, fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', color: '#9CA3AF' }}>{x.l}</p>
                                {x.meta != null && <p style={{ margin: 0, fontSize: 9, color: '#9CA3AF' }}>meta: {x.meta}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
                          <p style={{ margin: '0 0 8px', fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', color: '#9CA3AF' }}>
                            <i className="fas fa-headset" style={{ marginRight: 6 }} />
                            Conv. efectiva hoy — detalle por asesor
                            <span style={{ marginLeft: 8, color: '#D1D5DB' }}>(meta: {metas.meta_horas_conv || 4}h/asesor)</span>
                          </p>
                          <ConvEfectivaAsesores asesores={convEfectiva.asesores} meta={metas.meta_horas_conv || 4} />
                        </div>
                      </div>

                      {/* ── SECCIÓN 10: Histórico filtrable ── */}
                      <div style={cardStyle}>
                        <SH num="10" title="Histórico" sub="Evolución de leads, respuesta e inscritos con filtros" />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                          {/* Agrupación */}
                          <div style={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                            {[{ v: 'dia', l: 'Por día' }, { v: 'semana', l: 'Por semana' }, { v: 'mes', l: 'Por mes' }].map(o => (
                              <button key={o.v} onClick={() => setHistFiltros(f => ({ ...f, agrupacion: o.v }))}
                                style={btnTabStyle(histFiltros.agrupacion === o.v)}>
                                {o.l}
                              </button>
                            ))}
                          </div>
                          {/* Periodo */}
                          <div style={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                            {[{ v: '7d', l: '7 días' }, { v: '30d', l: '30 días' }, { v: '60d', l: '60 días' }, { v: '90d', l: '90 días' }].map(o => (
                              <button key={o.v} onClick={() => setHistFiltros(f => ({ ...f, preset: o.v }))}
                                style={btnTabStyle(histFiltros.preset === o.v)}>
                                {o.l}
                              </button>
                            ))}
                          </div>
                          {/* Asesor */}
                          <select value={histFiltros.asesor}
                            onChange={e => setHistFiltros(f => ({ ...f, asesor: +e.target.value }))}
                            className="form-control" style={{ width: 'auto', fontSize: 12 }}>
                            <option value={0}>Todos los asesores</option>
                            {asesores.map(a => <option key={a.id_usuario} value={a.id_usuario}>{a.nombre}</option>)}
                          </select>
                          {/* Fuente */}
                          <select value={histFiltros.fuente}
                            onChange={e => setHistFiltros(f => ({ ...f, fuente: +e.target.value }))}
                            className="form-control" style={{ width: 'auto', fontSize: 12 }}>
                            <option value={0}>Todas las fuentes</option>
                            {fuentes.map(f => <option key={f.id_fuente} value={f.id_fuente}>{f.nombre_fuente}</option>)}
                          </select>
                        </div>
                        {histLoading
                          ? <div style={{ textAlign: 'center', padding: '32px 0' }}><div className="spinner-border text-primary" role="status" style={{ width: 32, height: 32 }} /></div>
                          : histData.length > 1
                            ? <LineChart data={histData} keys={['leads', 'resp', 'conv']}
                                colors={['#1E5FD8', '#10B981', '#F97316']} labels={['Leads', 'Respuesta', 'Inscritos']}
                                height={200} agrupacion={histFiltros.agrupacion} />
                            : <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '32px 0', fontSize: 13 }}>Sin datos para los filtros seleccionados</p>
                        }
                      </div>

                      {/* ── SECCIÓN 11: Cierre mensual ── */}
                      <div style={cardStyle}>
                        <SH num="11" title="Cierre Mensual" sub="Comparativo de inscritos por asesor (últimos 3 meses)" />
                        <CierreChart cierres={cierres} meses={meses} />
                      </div>
                    </>
                  )}

                  {!loadingProd && !prodData && pidSel && (
                    <div style={cardStyle}>
                      <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '24px 0' }}>
                        <i className="fas fa-database" style={{ marginRight: 8 }} />Sin datos para este programa
                      </p>
                    </div>
                  )}
                </SpinnerLoading>
              </>
            )}
          </SpinnerLoading>
        </div>
      </div>

      {/* ── Modal metas ── */}
      {modalMetas && (
        <MetasModal
          idCamp={prodData?.id_camp || 0}
          metasActuales={metas}
          onClose={() => setModalMetas(false)}
          onSaved={() => pidSel && cargarProducto(pidSel)}
        />
      )}
    </Layout>
  )
}

export default ReporteKpisVentas
